import { CheckOutlined as CheckOutlinedIcon } from 'components/icons';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useGetPricelistByIdQuery, useGetPricelistPeriodImpactQuery } from 'store/api/pricelists';
import { CalculatedPricelist } from 'types/pricelists';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { PricelistDetailsFetchingCard } from '../PricelistDetailsFetchingCard';
import { inForcePeriodOf } from './employeePricing';
import { useToday } from './useEmployeePricing';

interface Props {
    pricelist: CalculatedPricelist;
    isSelected: boolean;
    onFollow: (pricelist: CalculatedPricelist) => void;
}

/**
 * An employee-priced list in the case editor's picker: picking it makes the case follow it rather than copy its
 * prices, so the card says so where a role-priced card shows the arrow of a copy.
 */
export function FollowablePricelistCard({ pricelist, isSelected, onFollow }: Props): JSX.Element {
    const today = useToday();
    const dateFormat = useEffectiveDateFormat();
    const { data: detail } = useGetPricelistByIdQuery(pricelist.id);
    const inForceFrom = inForcePeriodOf(detail?.periods, today)?.validFrom ?? null;
    // The list's connected cases are the open ones the caller may read; every follower counts here, a closed one too.
    const { data: impact } = useGetPricelistPeriodImpactQuery({
        id: pricelist.id,
        validFrom: [today],
        fromStart: false,
    });
    const followers = impact?.cases ?? 0;
    const follow = () => onFollow(pricelist);

    return (
        <div
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={follow}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    follow();
                }
            }}
            className={cn(
                'hover:bg-primary-surface hover:border-primary/40 mt-2.5 flex min-h-[50px] cursor-pointer items-center gap-2 rounded-[5px] border px-2.5 py-2',
                isSelected ? 'border-primary bg-primary-surface' : 'border-grey-300 bg-paper'
            )}
        >
            <div className="min-w-0 flex-1">
                <PricelistDetailsFetchingCard pricelist={pricelist} whiteSpace="nowrap" />
                <div className="text-grey-500 flex flex-wrap gap-x-2 text-[0.7rem]">
                    {/* A client's contract lists share the generated name, so the contract tells them apart. */}
                    {pricelist.contractName ? <span className="truncate">{pricelist.contractName}</span> : null}
                    {inForceFrom ? (
                        <span>
                            {t('pricelists.case-follow.period-in-force-from', {
                                date: format(parseISO(inForceFrom), dateFormat),
                            })}
                        </span>
                    ) : null}
                    {followers > 0 ? (
                        <span>{t('pricelists.case-follow.followers', { count: String(followers) })}</span>
                    ) : null}
                </div>
            </div>
            <span className="text-grey-600 flex shrink-0 items-center text-[0.72rem]">
                {isSelected ? <CheckOutlinedIcon /> : t('pricelists.case-follow.case-will-follow')}
            </span>
        </div>
    );
}
